import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface PopulationGrowthComparisonProps {
  location: string;
}

const PopulationGrowthComparison: React.FC<PopulationGrowthComparisonProps> = ({ location }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    let retryAttempts = 0;
    const maxRetries = 50;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [locationResult, californiaResult] = await Promise.all([
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', location)
            .gte('year', 1990)
            .lte('year', 2045)
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', 'California')
            .gte('year', 1990)
            .lte('year', 2045)
            .order('year', { ascending: true })
        ]);

        if (locationResult.error) {
          throw locationResult.error;
        }
        if (californiaResult.error) {
          throw californiaResult.error;
        }

        const locationData = locationResult.data || [];
        const californiaData = californiaResult.data || [];

        if (locationData.length === 0) {
          throw new Error('No population data found for the selected location');
        }
        if (californiaData.length === 0) {
          throw new Error('No population data found for California');
        }

        const locationByYear = new Map<number, number>();
        locationData.forEach(record => {
          if (record.year && record.population) {
            locationByYear.set(record.year, record.population);
          }
        });

        const californiaByYear = new Map<number, number>();
        californiaData.forEach(record => {
          if (record.year && record.population) {
            californiaByYear.set(record.year, record.population);
          }
        });

        const sharedYears = Array.from(locationByYear.keys())
          .filter(year => californiaByYear.has(year))
          .sort((a, b) => a - b);

        if (sharedYears.length === 0) {
          throw new Error('No overlapping population data between location and California');
        }

        const baselineYear = sharedYears[0];
        const locationBaseline = locationByYear.get(baselineYear) || 0;
        const californiaBaseline = californiaByYear.get(baselineYear) || 0;

        if (locationBaseline <= 0 || californiaBaseline <= 0) {
          throw new Error('Baseline population data is missing or invalid');
        }

        const chartLabels = sharedYears.map(year => year.toString());
        const locationIndex = sharedYears.map(year => {
          const value = locationByYear.get(year) || 0;
          return value > 0 ? ((value / locationBaseline) - 1) * 100 : null;
        });
        const californiaIndex = sharedYears.map(year => {
          const value = californiaByYear.get(year) || 0;
          return value > 0 ? ((value / californiaBaseline) - 1) * 100 : null;
        });

        const createChart = () => {
          if (isCancelled) {
            return;
          }

          if (!chartRef.current) {
            if (retryAttempts >= maxRetries) {
              return;
            }
            retryAttempts += 1;
            setTimeout(() => {
              if (!isCancelled) {
                createChart();
              }
            }, 100);
            return;
          }

          const ctx = chartRef.current.getContext('2d');
          if (!ctx) {
            if (retryAttempts >= maxRetries) {
              return;
            }
            retryAttempts += 1;
            setTimeout(() => {
              if (!isCancelled) {
                createChart();
              }
            }, 100);
            return;
          }

          if (chartInstance.current) {
            chartInstance.current.destroy();
          }

          chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
              labels: chartLabels,
              datasets: [
                {
                  label: `${location}`,
                  data: locationIndex,
                  borderColor: 'rgba(59, 130, 246, 1)',
                  backgroundColor: 'rgba(59, 130, 246, 0.12)',
                  borderWidth: 3,
                  tension: 0.1,
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  spanGaps: false
                },
                {
                  label: 'California',
                  data: californiaIndex,
                  borderColor: 'rgba(16, 185, 129, 1)',
                  backgroundColor: 'rgba(16, 185, 129, 0.12)',
                  borderWidth: 3,
                  tension: 0.1,
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  spanGaps: false
                }
              ]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              animation: false as const,
              plugins: {
                title: standardTitle(`Population Growth vs ${baselineYear} (0% baseline)`),
                legend: { ...standardLegend },
                tooltip: {
                  callbacks: {
                    label: (context: any) => {
                      const value = Number(context.parsed?.y ?? 0);
                      return `${context.dataset.label}: ${value.toFixed(1)}%`;
                    }
                  }
                }
              },
              scales: {
                x: {
                  title: {
                    display: true,
                    text: 'Year',
                    font: { weight: 'bold' as const }
                  },
                  ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    padding: 10
                  },
                  grid: { display: false }
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Percent Change from Baseline',
                    font: { weight: 'bold' as const }
                  },
                  grid: { color: 'rgba(0, 0, 0, 0.1)' },
                  ticks: {
                    callback: (value: any) => `${Number(value).toFixed(0)}%`
                  }
                }
              }
            }
          });

          if (chartInstance.current.resize) {
            chartInstance.current.resize = () => {};
          }
        };

        createChart();
        setLoading(false);
      } catch (err) {
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load population data');
          setLoading(false);
        }
      }
    };

    if (location) {
      fetchData();
    }

    return () => {
      isCancelled = true;
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location]);

  if (loading) {
    return (
      <div className="chart-container" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading population comparison data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'red' }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div
      id="population-growth-comparison-chart"
      style={{ height: '500px', width: '100%', position: 'relative' }}
    >
      <canvas
        ref={chartRef}
        style={{ height: '500px', width: '100%', display: 'block' }}
      />
    </div>
  );
};

export default PopulationGrowthComparison;
